'use client';

import { usePathname } from 'next/navigation';
import Link from '@/components/SiteLink';
import type { Article } from '@/server.api/types';

type NewsTickerProps = {
    articles: Article[];
    articleHrefs: Record<string, string>;
    articleCategorySlugs: string[];
    label: string;
    locale: string;
    paginationSegment: string;
    timeZone: string;
};

function formatTickerTime(date: string, locale: string, timeZone: string) {
    try {
        return new Intl.DateTimeFormat(locale, {
            hour: 'numeric',
            minute: '2-digit',
            timeZone,
        }).format(new Date(date));
    } catch {
        return date;
    }
}

export default function NewsTicker({
    articles,
    articleHrefs,
    articleCategorySlugs,
    label,
    locale,
    paginationSegment,
    timeZone,
}: NewsTickerProps) {
    const pathname = usePathname();
    if (articles.length === 0) return null;

    const segments = pathname.split('/').filter(Boolean);
    const isArticlePage =
        segments.length >= 2 &&
        segments[1] !== paginationSegment &&
        articleCategorySlugs.some((slug) => slug.replace(/^\/+|\/+$/g, '') === segments[0]);

    return (
        <aside
            className={`news-ticker${isArticlePage ? ' news-ticker--article' : ''}`}
            aria-labelledby='news-ticker-title'
        >
            <p id='news-ticker-title' className='news-ticker__label'>
                {label}
            </p>
            <div className='news-ticker__viewport'>
                <div className='news-ticker__track'>
                    {[false, true].map((clone) => (
                        <div
                            key={clone ? 'ticker-clone' : 'ticker-source'}
                            className='news-ticker__group'
                            aria-hidden={clone || undefined}
                        >
                            {articles.map((article) => (
                                <article
                                    key={`${article.id}-${clone}`}
                                    className='news-ticker__item'
                                >
                                    <time className='news-ticker__time' dateTime={article.date}>
                                        {formatTickerTime(article.date, locale, timeZone)}
                                    </time>
                                    <p className='news-ticker__headline'>
                                        <Link
                                            href={articleHrefs[article.id] ?? article.url}
                                            className='news-ticker__link'
                                            tabIndex={clone ? -1 : undefined}
                                        >
                                            {article.title}
                                        </Link>
                                    </p>
                                </article>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </aside>
    );
}
